<script setup>
// SiteUp: preferência por mundo (6 chaves). A migração de
// sidebar_hidden_menu_items → sidebar_hidden_worlds mora em hubWorlds.js:
// o mundo só fica oculto se TODOS os itens daquele mundo estavam ocultos.
// Tarefas/Agenda no header usam o mesmo padrão (lista hidden em uiSettings).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useHubWorlds } from 'dashboard/routes/dashboard/hub/useHubWorlds';
import { emitter } from 'shared/helpers/mitt';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { preferenceWorlds, hiddenWorldIds, setWorldVisible, showAllWorlds } =
  useHubWorlds();

const worlds = preferenceWorlds;

const isVisible = world => !world.hidden;

const visibleCount = computed(
  () => worlds.value.filter(world => isVisible(world)).length
);

const setVisibility = (world, visible) => {
  setWorldVisible(world.id, visible);
};

const hiddenHeaderItems = computed(() => {
  const hidden = uiSettings.value?.conversation_header_hidden_items;
  return Array.isArray(hidden) ? hidden : [];
});

const isHeaderActionVisible = name => !hiddenHeaderItems.value.includes(name);

const setHeaderActionVisible = (name, visible) => {
  const next = new Set(hiddenHeaderItems.value);
  if (visible) next.delete(name);
  else next.add(name);
  updateUISettings({ conversation_header_hidden_items: Array.from(next) });
};

const openOrganize = () => emitter.emit('workspace:organize:open');
</script>

<template>
  <div class="flex flex-col w-full gap-1">
    <div class="flex items-center justify-between pb-2">
      <span class="text-sm text-n-slate-11">
        {{
          t('HUB.PREFERENCES.VISIBLE', {
            visible: visibleCount,
            total: worlds.length,
          })
        }}
      </span>
      <div class="flex min-w-0 flex-wrap items-center justify-end gap-3">
        <button
          type="button"
          class="min-h-11 text-sm text-n-brand"
          data-testid="profile-organize"
          @click="openOrganize"
        >
          {{ t('WORKSPACE_V2.ORGANIZE') }}
        </button>
        <button
          type="button"
          class="min-h-11 text-sm text-n-brand disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!hiddenWorldIds.length"
          @click="showAllWorlds"
        >
          {{ t('HUB.PREFERENCES.SHOW_ALL') }}
        </button>
      </div>
    </div>
    <div
      v-for="world in worlds"
      :key="world.id"
      class="flex items-center justify-between py-2 border-t border-n-weak first:border-t-0"
    >
      <span class="text-sm text-n-slate-12">{{ t(world.labelKey) }}</span>
      <ToggleSwitch
        :model-value="isVisible(world)"
        @update:model-value="value => setVisibility(world, value)"
      />
    </div>
    <div class="flex flex-col gap-1 pt-4 mt-2 border-t border-n-weak">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('HUB.PREFERENCES.HEADER_TITLE') }}
      </span>
      <span class="text-xs text-n-slate-11 pb-1">
        {{ t('HUB.PREFERENCES.HEADER_DESCRIPTION') }}
      </span>
      <div class="flex items-center justify-between py-2">
        <span class="text-sm text-n-slate-12">
          {{ t('HUB.PREFERENCES.HEADER_TASKS') }}
        </span>
        <ToggleSwitch
          :model-value="isHeaderActionVisible('tasks')"
          data-testid="header-pref-tasks"
          @update:model-value="value => setHeaderActionVisible('tasks', value)"
        />
      </div>
      <div class="flex items-center justify-between py-2">
        <span class="text-sm text-n-slate-12">
          {{ t('HUB.PREFERENCES.HEADER_AGENDA') }}
        </span>
        <ToggleSwitch
          :model-value="isHeaderActionVisible('agenda')"
          data-testid="header-pref-agenda"
          @update:model-value="value => setHeaderActionVisible('agenda', value)"
        />
      </div>
    </div>
  </div>
</template>
